/**
 * src/main.tsx
 * ------------
 * Application entry point. Mounts the React app into the #root DOM node.
 *
 * Providers (outermost → innermost):
 *   1. React.StrictMode — double-renders in dev to surface side effects
 *   2. BrowserRouter    — client-side routing
 *   3. QueryClientProvider — TanStack Query for server state
 *   4. Suspense         — async chunk loading fallback
 *
 * Global CSS (Tailwind base + CSS variable themes) is imported here.
 */
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import App from './App'
import { queryClient } from './lib/queryClient'
import './lib/i18n'
import './index.css'

const rootElement = document.getElementById('root')
if (!rootElement) {
  throw new Error('Root element #root not found in index.html')
}

createRoot(rootElement).render(
  <StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <Suspense fallback={<div className="flex h-full items-center justify-center text-muted-foreground">Loading…</div>}>
          <App />
        </Suspense>
        {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
)
